import React, { useState } from 'react';
import { Wand2, Sparkles, Zap, Brain, Palette, MessageSquare } from 'lucide-react';
import { useCompany } from '../../lib/hooks';
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true
});

const WorkMagic = () => {
  const { company } = useCompany();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generatedContent, setGeneratedContent] = useState<string | null>(null);

  const features = [
    {
      icon: Sparkles,
      title: 'Work Order Analysis',
      description: 'Get AI insights on work order patterns and optimization',
      color: 'blue',
      prompt: `Analyze the common patterns in appliance repair work orders and suggest ways to improve efficiency and customer satisfaction for ${company?.name}. Focus on scheduling, parts management, and service quality.`
    },
    {
      icon: Palette,
      title: 'Service Optimization',
      description: 'Get AI suggestions for improving service delivery',
      color: 'purple',
      prompt: 'Suggest ways to optimize service delivery, reduce response times, and improve customer satisfaction. Include specific actionable recommendations.'
    },
    {
      icon: MessageSquare,
      title: 'Customer Responses',
      description: 'Draft professional responses to customer inquiries',
      color: 'green',
      prompt: 'Write a professional and empathetic response to a customer asking about appointment availability and pricing for appliance repair. Keep it concise but friendly.'
    },
    {
      icon: Brain,
      title: 'Service Insights',
      description: 'Get AI-powered insights about service trends and patterns',
      color: 'orange',
      prompt: 'Analyze common patterns in appliance repair businesses and suggest three innovative ways to improve customer satisfaction and operational efficiency.'
    }
  ];

  const handleFeatureClick = async (prompt: string) => {
    setLoading(true);
    setError(null);
    setGeneratedContent(null);

    try {
      const completion = await openai.chat.completions.create({
        messages: [{ role: "user", content: prompt }],
        model: "gpt-3.5-turbo",
      });

      setGeneratedContent(completion.choices[0]?.message?.content || null);
    } catch (err) {
      console.error('Error generating content:', err);
      setError('Failed to generate content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center space-x-3 mb-8">
        <div className="p-2 bg-gradient-to-br from-purple-500 to-blue-500 rounded-lg">
          <Wand2 className="w-6 h-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Work Magic</h1>
          <p className="text-gray-600">AI-powered tools to enhance your service business</p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {features.map((feature) => (
          <div
            key={feature.title}
            onClick={() => handleFeatureClick(feature.prompt)}
            className={`bg-white p-6 rounded-xl shadow-sm border border-gray-200 hover:shadow-md transition-all cursor-pointer group`}
          >
            <div className={`p-3 bg-${feature.color}-50 rounded-lg w-fit mb-4 group-hover:scale-110 transition-transform`}>
              <feature.icon className={`w-6 h-6 text-${feature.color}-500`} />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{feature.title}</h3>
            <p className="text-gray-600 text-sm">{feature.description}</p>
          </div>
        ))}
      </div>

      {(loading || error || generatedContent) && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {loading && (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 text-red-800 p-4 rounded-lg">
              {error}
            </div>
          )}

          {generatedContent && (
            <div className="prose max-w-none">
              <div className="bg-gray-50 p-6 rounded-lg">
                {generatedContent.split('\n').map((line, i) => (
                  <p key={i} className="mb-4 last:mb-0">{line}</p>
                ))}
              </div>
              <div className="flex justify-end mt-4">
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(generatedContent);
                    alert('Content copied to clipboard!');
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default WorkMagic;